// Shared mechanics and row markup of the LOADOUT and STOW sheets.
// Each sheet supplies its allowed kinds, capacity checks and words.

import { overlay as S } from '../../gen/strings';
import type { Action } from '../cards/card';
import { pressAttrs, stepper } from '../choice/choice';
import { esc } from '../esc';
import { countOf, totalOf, withCount, type Stock } from '../rounds';

/** A copy of saved rows as a draft the sheet may change. */
export function copyStock(rows: readonly Stock[]): Stock[] {
  return rows.map((r) => ({ kind: r.kind, count: r.count }));
}

/** Equal counts of every kind, independent of row order or duplicates. */
export function sameStock(a: readonly Stock[], b: readonly Stock[]): boolean {
  const kinds = new Set([...a, ...b].map((r) => r.kind));
  for (const k of kinds) if (countOf(a, k) !== countOf(b, k)) return false;
  return true;
}

/** One step, held at zero and at the capacity the sheet checked. */
export function stepStock(draft: readonly Stock[], kind: number, dir: number,
  kinds: readonly number[], canIncrease: boolean): Stock[] {
  if (!kinds.includes(kind)) return [...draft];
  const n = countOf(draft, kind);
  if (dir < 0) return n > 0 ? withCount(draft, kind, n - 1, kinds) : [...draft];
  return canIncrease ? withCount(draft, kind, n + 1, kinds) : [...draft];
}

/** Fill the free capacity with `kind`: as many items of `one` cargo as
 *  `room` cargo holds. A kind of no cargo fills nothing. */
export function maxStock(draft: readonly Stock[], kind: number, kinds: readonly number[],
  room: number, one: number): Stock[] {
  const free = one > 0 ? Math.floor(room / one) : 0;
  if (!kinds.includes(kind) || free <= 0) return [...draft];
  return withCount(draft, kind, countOf(draft, kind) + free, kinds);
}

/**
 * AUTO and SAVE of a sheet (R2031, R2654): `<prefix>auto` sends the empty
 * mix and stands lit and off while the saved mix is AUTO; `<prefix>set`
 * sends the draft and stands off for an empty draft or the saved one.
 */
export function stockActions(prefix: 'load' | 'stow', id: number, draft: readonly Stock[],
  saved: readonly Stock[]): Action[] {
  const auto = saved.length === 0;
  return [
    { act: `${prefix}auto`, id, face: 'quiet', label: S.word_auto, on: auto, off: auto },
    {
      act: `${prefix}set`, id, face: 'dark', label: S.btn_load_save,
      off: totalOf(draft) === 0 || sameStock(draft, saved),
    },
  ];
}

interface StockRow {
  kind: number;
  name: string;
  count: number;
  full: boolean;
  stepAct: string;
  maxAct: string;
  less: string;
  more: string;
  max: string;
  held: string;
  /** The cargo of one item under the name, or none (the LOADOUT sheet of
   *  a Sapper, R2708). */
  weight?: string;
}

/** The shared stepper, MAX press and held-stock line. */
export function stockRow(r: StockRow): string {
  const id = String(r.kind);
  return '<div class="ck-ldrow">'
    + `<span class="ck-ldname">${esc(r.name)}`
    + (r.weight ? `<small class="ck-ldw">${esc(r.weight)}</small>` : '') + '</span>'
    + stepper(String(r.count), r.stepAct, {
      id, label: r.name, prev: r.less, next: r.more, count: true,
      noPrev: r.count <= 0, noNext: r.full,
    })
    + `<button type="button" class="ch-arr ck-ldmax"${pressAttrs({ act: r.maxAct, id })}`
    + ` aria-label="${esc(r.max)}" title="${esc(r.max)}"${r.full ? ' disabled' : ''}>»</button>`
    + `<span class="ck-ldheld">${esc(r.held)}</span>`
    + '</div>';
}
